import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Mail, Ban, Settings, Info, Check, AlertTriangle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUnsubscribeGroups } from 'store/unsubscribeGroups';
import {
  clearUnsubscribePreferenceStatus,
  createUnsubscribePreferenceRequest,
  getPublicUnsubscribeGroupsRequest,
  getUnsubscribePreferenceRequest,
  selectUnsubscribePreference,
  selectUnsubscribePreferenceError,
  selectUnsubscribePreferenceLoading,
  selectUnsubscribePreferenceSuccess,
  updateUnsubscribePreferenceRequest,
} from 'store/unsubscribePreference';
import { useToast } from 'contexts/ToastContext';

const UnsubscribeMail: React.FC = () => {
  const { trackingId } = useParams<{ trackingId: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const unsubscribeGroups = useSelector(selectUnsubscribeGroups);
  const preference = useSelector(selectUnsubscribePreference);
  const loading = useSelector(selectUnsubscribePreferenceLoading);
  const error = useSelector(selectUnsubscribePreferenceError);
  const success = useSelector(selectUnsubscribePreferenceSuccess);
  const [unsubscribeAll, setUnsubscribeAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (trackingId) {
      dispatch(getPublicUnsubscribeGroupsRequest(trackingId));
      dispatch(getUnsubscribePreferenceRequest(trackingId));
    }
  }, [trackingId, dispatch]);

  useEffect(() => {
    if (preference) {
      setUnsubscribeAll(preference.unsubscribeAll ?? false);
    } else {
      setUnsubscribeAll(false);
    }
  }, [preference, unsubscribeGroups]);

  useEffect(() => {
    if (isSubmitting && !loading && trackingId) {
      if (error) {
        setIsSubmitting(false);
        showToast({
          type: 'error',
          title: 'Error',
          message: error,
          duration: 5000,
        });
        dispatch(clearUnsubscribePreferenceStatus());
      } else if (success) {
        setIsSubmitting(false);
        showToast({
          type: 'success',
          title: 'Success',
          message: 'Your email preferences have been updated successfully!',
          duration: 5000,
        });
        dispatch(clearUnsubscribePreferenceStatus());
        dispatch(getUnsubscribePreferenceRequest(trackingId));
      }
    }
  }, [loading, error, success, isSubmitting, showToast, dispatch, trackingId]);

  const handleUnsubscribeAll = () => {
    if (!trackingId) return;
    setIsSubmitting(true);
    const unsubscribeData = { unsubscribeAll: !unsubscribeAll };
    if (preference?._id) {
      dispatch(updateUnsubscribePreferenceRequest(trackingId, unsubscribeData));
    } else {
      dispatch(createUnsubscribePreferenceRequest(trackingId, unsubscribeData));
    }
  };

  const handleManagePreference = () => {
    navigate(`/unsubscribePreferences/${trackingId}`);
  };

  if (!trackingId || trackingId === 'null') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-primary-background to-info-background flex items-center justify-center py-6 sm:py-8 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-info/15 rounded-full blur-3xl" />
        </div>
        <div className="max-w-xl w-full mx-auto px-4 relative z-10">
          <div className="bg-card/95 backdrop-blur-md rounded-2xl shadow-xl border border-destructive/30 p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-destructive rounded-2xl mb-5 shadow-lg">
              <AlertTriangle
                className="w-7 h-7 text-destructive-foreground"
                strokeWidth={2.5}
              />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-3">
              Invalid Request
            </h1>
            <p className="text-muted-foreground mb-6">
              The tracking ID is missing or invalid. Please check the link and
              try again.
            </p>
            <p className="text-sm text-muted-foreground">
              If you believe this is an error, please contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const Footer = () => (
    <footer className="mt-8 text-center">
      <div className="inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 py-3 bg-card/80 backdrop-blur-md rounded-2xl shadow-md border border-border">
        <p className="text-xs text-muted-foreground font-medium">
          © 2025 SegMailer Inc.
        </p>
        <div className="flex items-center gap-4 text-xs">
          <a
            href="/legal"
            className="text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            Legal
          </a>
          <a
            href="/privacy"
            className="text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            Privacy
          </a>
          <a
            href="/cookies"
            className="text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            Manage cookies
          </a>
        </div>
      </div>
    </footer>
  );

  if (loading && !preference && unsubscribeGroups.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-primary-background to-info-background flex items-center justify-center py-6 sm:py-8 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-info/15 rounded-full blur-3xl" />
        </div>
        <div className="max-w-xl w-full mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-2xl mb-6 shadow-lg">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-primary-foreground border-t-transparent" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            Loading your preferences…
          </p>
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary-background to-info-background flex items-center justify-center py-6 sm:py-8 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-info/15 rounded-full blur-3xl" />
      </div>
      <div className="max-w-xl w-full mx-auto px-4 relative z-10">
        <header className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-2xl mb-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            aria-hidden
          >
            <Mail
              className="w-7 h-7 text-primary-foreground"
              strokeWidth={2.5}
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 tracking-tight">
            Update Your Email Preferences
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Choose how you'd like to manage your emails
          </p>
        </header>

        <main className="bg-card/95 backdrop-blur-md rounded-2xl shadow-xl border border-border p-6 sm:p-7 relative overflow-hidden mb-8 max-w-md mx-auto">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-background/40 via-transparent to-info-background/40 pointer-events-none rounded-2xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 px-3 py-2 bg-info-background rounded-xl mb-5">
              <Info className="w-4 h-4 text-info flex-shrink-0" />
              <p className="text-xs font-medium text-foreground">
                Choose how you'd like to manage your email preferences
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <section
                className="rounded-xl border border-destructive/30 bg-destructive-background/80 p-4 hover:border-destructive/50 transition-all duration-300"
                aria-labelledby="unsubscribe-all-heading"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-destructive rounded-lg flex items-center justify-center shadow-md">
                    <Ban
                      className="w-5 h-5 text-destructive-foreground"
                      strokeWidth={2.5}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2
                      id="unsubscribe-all-heading"
                      className="text-base font-semibold text-foreground mb-1"
                    >
                      Unsubscribe from All Emails
                    </h2>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Stop receiving all marketing emails. You won't receive
                      promotional content, newsletters, or updates.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleUnsubscribeAll}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-destructive text-destructive-foreground font-semibold rounded-xl shadow-md hover:bg-destructive-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                >
                  {isSubmitting ? (
                    <div
                      className="animate-spin rounded-full h-4 w-4 border-2 border-destructive-foreground border-t-transparent"
                      aria-hidden
                    />
                  ) : unsubscribeAll ? (
                    <Check className="w-4 h-4" aria-hidden />
                  ) : (
                    <Ban className="w-4 h-4" aria-hidden />
                  )}
                  {unsubscribeAll
                    ? 'Subscribe to All Emails'
                    : 'Unsubscribe from All Emails'}
                </button>
              </section>

              <section
                className="rounded-xl border border-border bg-card/80 p-4 hover:border-primary/40 hover:shadow-md transition-all duration-300"
                aria-labelledby="manage-prefs-heading"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-md">
                    <Settings
                      className="w-5 h-5 text-primary-foreground"
                      strokeWidth={2.5}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2
                      id="manage-prefs-heading"
                      className="text-base font-semibold text-foreground mb-1"
                    >
                      Manage Your Preferences
                    </h2>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Choose which types of emails you want. Customize
                      newsletters, promotions, and updates.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleManagePreference}
                  className="w-full px-4 py-3 bg-primary text-primary-foreground font-semibold rounded-xl shadow-md hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                >
                  <Settings className="w-4 h-4" aria-hidden />
                  Manage Preference
                </button>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default UnsubscribeMail;
